import { useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CyclesSettings } from "@/components/cycles/cycles-settings";
import { ImageField } from "@/components/settings/image-field";
import { SettingsSection } from "@/components/settings/section";
import { useWorkspace } from "@/components/shell/workspace-context";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";
import { NAME_MAX, SLUG_MAX, checkSlug, cleanName } from "@/lib/slugs";

export const Route = createFileRoute("/_app/$workspaceSlug/settings/workspace")({
  component: WorkspaceSettings,
});

function WorkspaceSettings() {
  const { t } = useTranslation("settings");
  const { t: tErrors } = useTranslation("errors");
  const { t: tCommon } = useTranslation();
  const { workspace } = useWorkspace();
  const canEdit = workspace.role !== "member";
  const [name, setName] = useState(workspace.name);
  const [slug, setSlug] = useState(workspace.slug);
  const [nameError, setNameError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const cleaned = cleanName(name);
  const dirty = cleaned !== workspace.name || slug !== workspace.slug;
  const generateImageUploadUrl = useMutation(api.workspaces.generateImageUploadUrl);
  const setImage = useMutation(api.workspaces.setImage);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const slugProblem = slug === workspace.slug ? null : checkSlug(slug);
    setNameError(cleaned ? null : tErrors("nameInvalid"));
    setSlugError(slugProblem ? tErrors(slugProblem) : null);
    setError(null);
    if (!cleaned || slugProblem) return;

    setPending(true);
    const result = await authClient.organization.update({
      organizationId: workspace.organizationId,
      data: { name: cleaned, slug },
    });
    setPending(false);
    if (result.error) {
      const message = authErrorMessage(result.error, tErrors);
      if (result.error.code?.includes("SLUG")) setSlugError(message);
      else setError(message);
      return;
    }
    // A new address needs no navigation from here: the workspace layout
    // follows the workspace to it as soon as the change arrives.
    toast(t("saved"));
  }

  return (
    <>
      <SettingsSection
        title={t("workspace.title")}
        description={canEdit ? undefined : t("workspace.readOnly")}
      >
        <form onSubmit={onSubmit} noValidate className="flex max-w-sm flex-col gap-4">
          <ImageField
            name={workspace.name}
            image={workspace.image}
            canEdit={canEdit}
            generateUploadUrl={() => generateImageUploadUrl({ workspaceId: workspace._id })}
            setImage={(storageId) => setImage({ workspaceId: workspace._id, storageId })}
          />
          <Field label={t("workspace.name")} htmlFor="workspace-name" error={nameError}>
            <Input
              id="workspace-name"
              autoComplete="organization"
              maxLength={NAME_MAX}
              value={name}
              disabled={!canEdit}
              aria-invalid={Boolean(nameError)}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field
            label={t("workspace.slug")}
            htmlFor="workspace-slug"
            error={slugError}
            hint={canEdit ? t("workspace.slugHint", { address: `${window.location.host}/${slug}` }) : undefined}
          >
            <Input
              id="workspace-slug"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={SLUG_MAX}
              value={slug}
              disabled={!canEdit}
              aria-invalid={Boolean(slugError)}
              onChange={(event) => setSlug(event.target.value.toLowerCase())}
            />
          </Field>
          <FormError>{error}</FormError>
          {canEdit ? (
            <div>
              <Button type="submit" disabled={pending || !dirty}>
                {pending ? <Spinner /> : null}
                {tCommon("actions.save")}
              </Button>
            </div>
          ) : null}
        </form>
      </SettingsSection>
      <CyclesSettings workspaceId={workspace._id} />
    </>
  );
}
